// src/pages/Ligue/Annonces/EditAnnonceModal.jsx
import React, { useEffect, useState } from "react";
import { FaSave, FaTimes } from "react-icons/fa";
import Button from "./../../../components/Button/Button";
import { updateAnnonce } from "./../../../services/annoncesServices";
import ReactQuill from "react-quill";
import "react-quill/dist/quill.snow.css";
import { useNotification } from "../../../components/Notification/NotificationManager";
import { Modal, ModalHeader, ModalBody, ModalFooter } from "../../../components/Modal/Modal";

const CATEGORIES = [
  "Recherche Enseignant",
  "Enseignant cherche club",
  "Recherche Matériel",
  "Don Matériel",
  "Autres",
];

const EditAnnonceModal = ({ isOpen, onClose, annonce, fetchAnnonces }) => {
  const { addNotification } = useNotification();
  const [saving, setSaving] = useState(false);
  const [formData, setFormData] = useState({
    id: "",
    category: "",
    title: "",
    content: "",
    image_url: "",
    club_user: "",
    tel_user: "",
    mail_user: "",
    date_annonce: "",
    is_active: true,
  });

  useEffect(() => {
    if (!isOpen) return;
    if (annonce) {
      setFormData({
        id: annonce.id,
        category: annonce.category || "",
        title: annonce.title || "",
        content: annonce.content || "",
        image_url: annonce.image_url || "",
        club_user: annonce.club_user || "",
        tel_user: annonce.tel_user || "",
        mail_user: annonce.mail_user || "",
        date_annonce: annonce.date_annonce || new Date().toISOString().slice(0, 10),
        is_active: !!annonce.is_active,
      });
    }
  }, [isOpen, annonce]);

  const handleChange = (e) => {
    const { name, type, checked, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: type === "checkbox" ? checked : value }));
  };

  const handleContentChange = (value) => setFormData((p) => ({ ...p, content: value }));

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      setSaving(true);
      await updateAnnonce(formData);
      await fetchAnnonces?.();
      onClose?.();
      addNotification("Annonce mise à jour avec succès", "success");
    } catch (error) {
      console.error("Erreur lors de la mise à jour de l'annonce", error);
      addNotification("Erreur lors de la mise à jour de l'annonce", "error");
    } finally {
      setSaving(false);
    }
  };

  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      size="lg"
      labelledById="edit-annonce-title"
      describedById="edit-annonce-desc"
      initialFocusSelector="#edit-annonce-title-input"
    >
      <ModalHeader
        id="edit-annonce-title"
        title="Mettre à jour l’annonce"
        subtitle="Modifiez les champs nécessaires puis enregistrez"
        onClose={onClose}
      />
      <ModalBody id="edit-annonce-desc">
        <form onSubmit={handleSubmit} className="space-y-4">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <Field label="Catégorie" required>
              <select
                name="category"
                value={formData.category}
                onChange={handleChange}
                required
                className="w-full rounded-xl border border-slate-300 dark:border-slate-700 bg-white/80 dark:bg-slate-900/70 px-3 py-2"
              >
                <option value="" disabled>Choisir…</option>
                {CATEGORIES.map((c) => (
                  <option key={c} value={c}>{c}</option>
                ))}
              </select>
            </Field>

            <Field label="Titre" required>
              <input
                id="edit-annonce-title-input"
                name="title"
                value={formData.title}
                onChange={handleChange}
                required
                autoComplete="off"
                className="w-full rounded-xl border border-slate-300 dark:border-slate-700 bg-white/80 dark:bg-slate-900/70 px-3 py-2"
              />
            </Field>
          </div>

          <Field label="Contenu" required>
            <ReactQuill value={formData.content} onChange={handleContentChange} className="bg-white dark:bg-slate-900/70 rounded-xl" />
          </Field>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <Field label="URL de l'image">
              <input
                name="image_url"
                type="url"
                value={formData.image_url}
                onChange={handleChange}
                autoComplete="off"
                placeholder="https://…"
                className="w-full rounded-xl border border-slate-300 dark:border-slate-700 bg-white/80 dark:bg-slate-900/70 px-3 py-2"
              />
            </Field>
            <Field label="Club">
              <input
                name="club_user"
                value={formData.club_user}
                onChange={handleChange}
                autoComplete="off"
                placeholder="Nom du club"
                className="w-full rounded-xl border border-slate-300 dark:border-slate-700 bg-white/80 dark:bg-slate-900/70 px-3 py-2"
              />
            </Field>
            <Field label="Téléphone">
              <input
                name="tel_user"
                value={formData.tel_user}
                onChange={handleChange}
                autoComplete="off"
                placeholder="06 12 34 56 78"
                className="w-full rounded-xl border border-slate-300 dark:border-slate-700 bg-white/80 dark:bg-slate-900/70 px-3 py-2"
              />
            </Field>
            <Field label="Email">
              <input
                type="email"
                name="mail_user"
                value={formData.mail_user}
                onChange={handleChange}
                autoComplete="off"
                placeholder="contact@exemple.fr"
                className="w-full rounded-xl border border-slate-300 dark:border-slate-700 bg-white/80 dark:bg-slate-900/70 px-3 py-2"
              />
            </Field>

            <Field label="Date de l'annonce">
              <input
                type="date"
                name="date_annonce"
                value={formData.date_annonce?.slice(0, 10) || ""}
                onChange={handleChange}
                className="w-full rounded-xl border border-slate-300 dark:border-slate-700 bg-white/80 dark:bg-slate-900/70 px-3 py-2"
              />
            </Field>

            <Field label="Publication">
              <label className="inline-flex items-center gap-2 text-sm">
                <input
                  type="checkbox"
                  name="is_active"
                  checked={!!formData.is_active}
                  onChange={handleChange}
                  className="checkbox"
                />
                Afficher sur la page
              </label>
            </Field>
          </div>

          <ModalFooter>
            <Button
              text="Annuler"
              onClick={onClose}
              icon={FaTimes}
              type="button"
              className="!rounded-xl !bg-slate-100 !text-slate-800 hover:!bg-slate-200 dark:!bg-slate-800 dark:!text-slate-100 dark:hover:!bg-slate-700 gap-2"
            />
            <Button
              text={saving ? "Enregistrement…" : "Mettre à jour"}
              icon={FaSave}
              type="submit"
              disabled={saving}
              className="!rounded-xl gap-2"
            />
          </ModalFooter>
        </form>
      </ModalBody>
    </Modal>
  );
};

function Field({ label, required, children }) {
  return (
    <div>
      <label className="text-sm font-medium text-slate-700 dark:text-slate-200">
        {label} {required ? <span className="text-pink-600">*</span> : null}
      </label>
      <div className="mt-1">{children}</div>
    </div>
  );
}

export default EditAnnonceModal;
